import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { array } from 'prop-types';

import { useDispatch } from 'react-redux';
import styles from '../MarketDetail/index.module.scss';
import {
  DEFAULT_CENTER_MAP,
  largeIndent,
  smallIndent,
} from '../../../utils/constants';
import {
  fetchAutoSuggestText,
  setCurrentPlace,
} from '../../../store/market/actions';
import { numberWithCommas } from '../../../utils/helpers/thousandsSeparator';
import { numberAfterPoint } from '../../../utils/helpers/numberAfterPoint';

const Map = dynamic(() => import('../../common/Map/index'), {
  ssr: false,
});

const MarketView = ({ options }) => {
  const dispatch = useDispatch();

  const searchCurrentCity = useMemo(() => [...options], [options]);
  const [selectedOption] = useState('Wohnlage');

  useEffect(() => {
    dispatch(fetchAutoSuggestText(searchCurrentCity[0]?.url.split('-')[0]))
      .then((res) => {
        if (res?.data?.data?.length) {
          const searchCurrentPlace = res.data.data.find(
            (item) => item.kgs === options[0].kgs,
          );
          dispatch(setCurrentPlace(searchCurrentPlace));
        } else {
          dispatch(
            setCurrentPlace({
              fulltitle: searchCurrentCity[0].name,
              urlpath: searchCurrentCity[0].url,
            }),
          );
        }
      })
      .catch((e) => console.log(e));
  }, [searchCurrentCity, options, dispatch]);

  const renderOption = useCallback((option) => {
    if (option['0']?.angebotspreise_monat_fair) {
      const numAfterPoint = numberAfterPoint(
        option['0'].angebotspreise_monat_fair,
      );
      if (numAfterPoint > 0) {
        const number = option['0'].angebotspreise_monat_fair.toFixed(2);
        return `€ ${numberWithCommas(number)}`;
      }
      if (numAfterPoint === 0) {
        return `€ ${numberWithCommas(
          option['0'].angebotspreise_monat_fair,
        )},00`;
      }
    }
    return '€ 0';
  }, []);

  const renderCityLink = useCallback(
    (option, idx) => (
      <div className={styles.citiesBlock} key={idx}>
        <Link href={`/mietspiegel/${encodeURIComponent(option?.url)}`} passHref>
          <a data-cy="marktspiegel-link" className="g-color-gray-dark-v3">
            {option.name}
          </a>
        </Link>
        <span data-cy="marktspiegel-preis">{renderOption(option)}</span>
      </div>
    ),
    [renderOption],
  );

  return (
    <div className={classNames('container', styles.marketContainer)}>
      <div
        style={{
          paddingBottom: `${smallIndent}rem`,
          paddingTop: `${largeIndent - 0.5625}rem`,
        }}
      >
        <h1
          style={{ marginBottom: `${smallIndent - 0.5625}rem` }}
          className="g-color-gray-dark-v3 text-start text-uppercase g-font-weight-700 g-pr-20"
        >
          Marktmietspiegel in Deutschland
        </h1>
        <h3
          style={{ paddingBottom: `${smallIndent}rem` }}
          className="h3 g-color-gray-dark-v3 text-uppercase"
        >
          die gefragtesten Lagen
        </h3>
        <div className={classNames('row no-gutters', styles.marketBlock)}>
          <div
            className={classNames(
              'd-flex align-items-end g-color-gray-dark-v3 col-4 g-pr-40 g-pl-0 g-mb-0--lg',
              styles.textPositionLeftBlockMarket,
            )}
          >
            <div className=" d-flex flex-column g-color-gray-dark-v3">
              <p className="mb-0">
                Rechtlich verbindlich ist immer nur der amtliche Mietspiegel,
                sprechen Sie hierzu Ihre Gemeinde-/Stadtverwaltung an.
              </p>
              <div
                className={classNames(
                  'list-unstyled g-pt-20',
                  styles.cityContainer,
                )}
              >
                {options?.map(renderCityLink)}
              </div>
            </div>
          </div>
          <div
            className={classNames(
              'col-8 g-mb-50 g-mb-0--lg pr-0 g-pl-10',
              styles.iframeBlock,
            )}
          >
            <Map
              mapId="market-view-map"
              center={DEFAULT_CENTER_MAP}
              height="630px"
              isShowLegend
              selectedLayer={selectedOption}
            />
          </div>
        </div>
        <div className="g-pt-40" />
      </div>
    </div>
  );
};

MarketView.propTypes = {
  options: array.isRequired,
};

export default MarketView;
